import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import React, {ReactElement} from "react";
import {DIVIDER} from "../shared/dimens";
import {ButtonBase, Grid, Tooltip} from "@mui/material";
import {StyledOrientationComponent} from "../shared/StyledComponents";
import {Orientation} from "../shared/types";

export type ToolOption = {
  id: string,
  text?: string,
  image: string,
}

export type ToolOptions = {
  title: string,
  options: ToolOption[],
}

export type Tool = {
  id: string,
  text: string,
  image: string | "preview",
  toolOptions: ToolOptions,
  defaultToolOptionId: string,
}

export interface PPNThreePaneFragmentListener {
  onToolSelected(toolId: string): void;

  onToolOptionsSelected(toolOptionIds: Map<string, string>): void;
}

export type PeppinThreePaneFragmentProps = BaseFragmentProps & {
  tools: Tool[],
  defaultToolOptions?: Map<string, string>,
  toolsPaneSize?: "sm" | "md" | "lg",
  listener: PPNThreePaneFragmentListener,
  children: any,
}

type PeppinThreePaneFragmentState = BaseFragmentState & {
  selectedToolId?: string,
  selectedToolOptionIds?: Map<string, string>,
}

const DIVIDER_SELECT = "4px solid black";
const DIVIDER_NOSELECT = "4px solid transparent";

export class PeppinThreePaneFragment extends BaseFragment<PeppinThreePaneFragmentProps, PeppinThreePaneFragmentState> {

  setSelectedToolOptionIds(selectedToolOptionIds: Map<string, string>) {
    this.setState({
      selectedToolOptionIds
    });
  }

  protected containerContentDidRender() {
    super.containerContentDidRender();
    let selectedToolOptionIds;
    if (this.props.defaultToolOptions) {
      selectedToolOptionIds = this.props.defaultToolOptions;
    } else {
      selectedToolOptionIds = new Map<string, string>();
      this.props.tools.forEach(tool => selectedToolOptionIds.set(tool.id, tool.defaultToolOptionId));
    }
    this.setState({
      selectedToolId: this.props.tools[0].id,
      selectedToolOptionIds: selectedToolOptionIds,
    });
  }

  componentDidUpdate(prevProps: Readonly<PeppinThreePaneFragmentProps>, prevState: Readonly<PeppinThreePaneFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.selectedToolId !== this.state.selectedToolId) {
      this.props.listener.onToolSelected(this.state.selectedToolId);
    }
    if (prevState.selectedToolOptionIds !== this.state.selectedToolOptionIds) {
      this.props.listener.onToolOptionsSelected(this.state.selectedToolOptionIds)
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    if (!this.state.selectedToolId) {
      return null;
    }
    const selectedTool = this.props.tools.find(tool => tool.id === this.state.selectedToolId);
    const selectedToolOption = selectedTool.toolOptions.options.find(option => option.id === this.state.selectedToolOptionIds.get(selectedTool.id));
    return <StyledOrientationComponent
      breakpoint={"md"}
      render={orientation => <div
        style={{
          display: "flex",
          flexDirection: orientation === Orientation.LANDSCAPE ? "row" : "column",
          width: "100vw",
          height: "100vh",
          paddingTop: 72
        }}>
        {orientation === Orientation.LANDSCAPE
          ? this.renderLandscape(selectedTool, selectedToolOption)
          : this.renderPortrait(selectedTool, selectedToolOption)}
      </div>}/>
  }

  private renderLandscape(selectedTool: Tool, selectedToolOption: ToolOption): ReactElement {
    return <>
      <div
        className="hidescroll"
        style={{
          display: "flex", flexDirection: "column", width: 72, flexShrink: 0, borderRight: DIVIDER, overflowY: "scroll",
        }}>
        {this.renderTools(selectedTool, Orientation.LANDSCAPE)}
      </div>
      <div
        className="hidescroll"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: 360,
          flexShrink: 0,
          borderRight: DIVIDER,
          overflow: "scroll",
        }}>
        <Grid container>
          {this.renderToolOptions(selectedTool, selectedToolOption, Orientation.LANDSCAPE)}
        </Grid>
      </div>
      <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
        {this.props.children}
      </div>
    </>;
  }

  private renderPortrait(selectedTool: Tool, selectedToolOption: ToolOption): ReactElement {
    return <>
      <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
        {this.props.children}
      </div>
      <div
        className="hidescroll"
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: 360,
          flexShrink: 0,
          borderTop: DIVIDER,
          overflow: "scroll",
        }}>
        <Grid container>
          {this.renderToolOptions(selectedTool, selectedToolOption, Orientation.PORTRAIT)}
        </Grid>
      </div>
      <div
        className="hidescroll"
        style={{
          display: "flex", flexDirection: "row", height: 72, flexShrink: 0, borderTop: DIVIDER, overflowX: "scroll",
        }}>
        {this.renderTools(selectedTool, Orientation.PORTRAIT)}
      </div>
    </>;
  }

  private renderToolOptions(selectedTool: Tool, selectedToolOption: ToolOption, orientation: Orientation) {
    return <>
      {selectedTool.toolOptions.options.map(option => <Grid xs={4}>
        <ButtonBase
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: selectedToolOption.id === option.id ? DIVIDER_SELECT : DIVIDER_NOSELECT
          }}
          onClick={() => {
            const map = new Map<string, string>(this.state.selectedToolOptionIds.entries());
            map.set(selectedTool.id, option.id);
            this.setState({selectedToolOptionIds: map});
          }}>
          <Tooltip title={option.text}>
            <img src={option.image}
                 style={{
                   width: "100%",
                   aspectRatio: 1,
                   padding: 12,
                   boxSizing: "border-box",
                   objectFit: "contain"
                 }}/>
          </Tooltip>
        </ButtonBase>
      </Grid>)}
    </>;
  }

  private renderTools(selectedTool: Tool, orientation: Orientation) {
    return <>
      {this.props.tools.map(tool => <ButtonBase
        style={{boxSizing: "border-box", border: selectedTool.id === tool.id ? DIVIDER_SELECT : DIVIDER_NOSELECT}}
        onClick={() => this.setState({selectedToolId: tool.id})}>
        <Tooltip title={tool.text} placement={orientation === Orientation.LANDSCAPE ? "right" : "top"}>
          <img
            src={tool.image === "preview" ? tool.toolOptions.options.find(option => option.id === this.state.selectedToolOptionIds.get(tool.id)).image : tool.image}
            style={{width: 72, height: 72, padding: 12, boxSizing: "border-box", objectFit: "contain"}}/>
        </Tooltip>
      </ButtonBase>)}
    </>;
  }
}